import { SUPABASE_URL } from '../utils/supabase/url';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/supabase/client';
import {assertEventActor,getEventActorHeaders} from './eventActorRequest';
import type { UnsignedDayworkTally } from '../contract-administration/dayworkSignOff';

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b/dayworks`;

/**
 * Rate basis dictates how the build-up reconciles back to the contract:
 *   jct_tender_rates — JCT family, value at tendered rates / similar work
 *   nec_scc          — NEC, defined cost via Schedule of Cost Components + fee
 *   rics_dpc         — *RICS Definition of Prime Cost of Daywork* (default)
 *   bespoke          — anything else, free-text reconciliation in notes
 *
 * [Change Control 2024, pp. 24, 29]
 */
export type DayworkRateBasis = 'jct_tender_rates' | 'nec_scc' | 'rics_dpc' | 'bespoke';

export interface DayworkLabourRow {
  id?: string;
  trade: string;
  quantity: number;
  hours: number;
  rate: number;
}

export interface DayworkMaterialRow {
  id?: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  /** Optional substantiation refs — surfaced in the form & PDF. */
  supplierRef?: string | null;
  poRef?: string | null;
  invoiceRef?: string | null;
}

export interface DayworkPlantRow {
  id?: string;
  description: string;
  quantity: number;
  hours: number;
  rate: number;
}

export interface ApiDaywork {
  financialRedacted?: boolean;
  signatureAcknowledgement?: 'quantities_only' | null;
  signatureRecordedAt?: string | null;
  signatureRecordedBy?: string | null;
  id: string;
  projectId: string;
  workspaceId: string | null;
  referenceNumber: string;
  description: string;
  date: string;
  instructionReference: string | null;
  notes: string | null;
  labour: DayworkLabourRow[];
  materials: DayworkMaterialRow[];
  plant: DayworkPlantRow[];
  clientSignature: string | null;
  signedBy: string | null;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  clientApprovalDate: string | null;
  /** Quantis Field approval workflow; independent of `status`. Absent until its migration is applied. */
  approvalState?: 'draft' | 'submitted' | 'approved' | 'declined' | 'changes_requested';
  submittedAt?: string | null;
  attachedApplicationId: string | null;
  instructionId: string | null;
  /** Parent event, when this daywork was raised from / attached to one. */
  eventId: string | null;

  // ── Subcontractor-perspective additions (May 2026, see spec) ─────────────
  rateBasis: DayworkRateBasis;
  ohPercent: number;
  incidentalsPercent: number;
  isLossAndExpenseSeparate: boolean;
  retentionApplicable: boolean;
  signatureRefused: boolean;
  signatureRefusedReason: string | null;
  signatureRefusedAt: string | null;
  noticeIssuedAt: string | null;
  particularsIssuedAt: string | null;

  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type DayworkWriteInput = Omit<Partial<ApiDaywork>, 'labour'|'materials'|'plant'> & {
  expectedUpdatedAt?: string;
  labour?: Array<Omit<DayworkLabourRow,'rate'> & {rate?:number}>;
  materials?: Array<Omit<DayworkMaterialRow,'rate'> & {rate?:number}>;
  plant?: Array<Omit<DayworkPlantRow,'rate'> & {rate?:number}>;
};
export interface DayworkSiteSignatureInput {signature:string;signedBy:string;expectedUpdatedAt:string;acknowledgement:'quantities_only'}

export interface ApiSignatureRequest {
  id: string;
  dayworkId: string;
  token: string;
  recipientEmail: string;
  recipientName: string;
  recipientRole: string | null;
  status: 'sent' | 'signed' | 'expired' | 'revoked';
  signature: string | null;
  signedAt: string | null;
  expiresAt: string;
  createdBy: string | null;
  createdAt: string;
}

async function authedFetch(url: string, init?: RequestInit, actorId?:string) {
  const headers = actorId ? await getEventActorHeaders(actorId, {'Content-Type':'application/json'}) : await getAuthHeaders({'Content-Type':'application/json'});
  if(actorId)await assertEventActor(actorId);
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
  if(actorId)await assertEventActor(actorId);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return res;
}

export const DayworkService = {
  /** Dayworks waiting for client sign-off, per project the caller may read, for one workspace. */
  async unsigned(workspaceId: string): Promise<Record<string, UnsignedDayworkTally>> {
    const res = await authedFetch(`${API_BASE}/unsigned?workspaceId=${encodeURIComponent(workspaceId)}`);
    return ((await res.json()) as { counts: Record<string, UnsignedDayworkTally> }).counts;
  },

  async list(projectId: string): Promise<ApiDaywork[]> {
    const res = await authedFetch(`${API_BASE}?projectId=${encodeURIComponent(projectId)}`);
    const data = await res.json();
    return (data.dayworks ?? []) as ApiDaywork[];
  },

  async create(input: DayworkWriteInput & { projectId: string }): Promise<ApiDaywork> {
    const res = await authedFetch(API_BASE, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await res.json();
    return data.daywork as ApiDaywork;
  },

  async update(id: string, patch: DayworkWriteInput): Promise<ApiDaywork> {
    const res = await authedFetch(`${API_BASE}/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
    const data = await res.json();
    return data.daywork as ApiDaywork;
  },

  async captureSiteSignature(id:string,input:DayworkSiteSignatureInput,actorId?:string):Promise<ApiDaywork>{
    const response=await authedFetch(`${API_BASE}/${encodeURIComponent(id)}/site-signature`,{method:'POST',body:JSON.stringify(input)},actorId);
    const data=await response.json();
    if(actorId)await assertEventActor(actorId);
    return data.daywork as ApiDaywork;
  },

  async remove(id: string): Promise<void> {
    await authedFetch(`${API_BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  async listSignatureRequests(dayworkId: string): Promise<ApiSignatureRequest[]> {
    const res = await authedFetch(`${API_BASE}/${encodeURIComponent(dayworkId)}/signature-requests`);
    const data = await res.json();
    return (data.requests ?? []) as ApiSignatureRequest[];
  },

  async requestSignature(
    dayworkId: string,
    input: { recipientEmail: string; recipientName: string; recipientRole?: string },
  ): Promise<ApiSignatureRequest> {
    const res = await authedFetch(`${API_BASE}/${encodeURIComponent(dayworkId)}/signature-requests`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await res.json();
    return data.request as ApiSignatureRequest;
  },
};
